import React, { useState, useEffect, useMemo } from 'react';
import { api } from '@/lib/api';
import { MonthlyConsumptionSlide } from '@/components/MonthlyConsumptionSlide';
import { MultiSelectFilter } from '@/components/MultiSelectFilter';
import { Skeleton } from '@/components/ui/skeleton';
import { Filter } from 'lucide-react';

export default function MonthlyConsumption() {
  const [projects, setProjects] = useState([]);
  const [adhocInvoices, setAdhocInvoices] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.getProjects(), api.getAdhocInvoices()])
      .then(([projs, adhocs]) => { setProjects(projs); setAdhocInvoices(adhocs); setLoading(false); })
      .catch(() => { setProjects([]); setAdhocInvoices([]); setLoading(false); });
  }, []);

  const clients = useMemo(() => {
    const names = [...new Set(projects.map(p => p.clientName).filter(Boolean))].sort();
    return names.map(n => ({ value: n, label: n }));
  }, [projects]);

  return (
    <div data-testid="monthly-consumption-page">
      {loading ? (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="space-y-3">
              <Skeleton className="h-10 w-80" />
              <Skeleton className="h-4 w-96" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-48" />
            </div>
          </div>

          {/* Consumption Slides - Multiple cards for each slide */}
          <div className="space-y-6">
            {/* Slide 1 */}
            <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Skeleton className="h-64 w-full rounded-lg" />
                <Skeleton className="h-64 w-full rounded-lg" />
              </div>
            </div>

            {/* Slide 2 */}
            <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-96 w-full rounded-lg" />
            </div>

            {/* Slide 3 */}
            <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-80 w-full rounded-lg" />
            </div>
          </div>
        </div>
      ) : (
        <>
      <div className="flex flex-col gap-4 items-start md:items-center md:justify-between md:flex-row mb-6">
        <div>
          <h1 className="font-manrope font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight text-white" data-testid="consumption-title">
            Monthly Consumption
          </h1>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1 font-manrope">Financial year overview & monthly insights</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          <MultiSelectFilter
            label="Clients"
            options={clients}
            selected={selectedClients}
            onSelectionChange={setSelectedClients}
            testId="consumption-client-filter"
          />
        </div>
      </div>
      <MonthlyConsumptionSlide projects={projects} adhocInvoices={adhocInvoices} selectedClients={selectedClients} />
        </>
      )}
    </div>
  );
}
